import { memo, useMemo } from 'react';
import { InlineFieldContainer } from 'common/components/controls/InlineField/InlineFieldContainer';
import DueDateField from 'common/components/dueDate';
import DatePicker from 'common/components/datePicker';
import Tooltip from 'common/components/tooltip';
import { useTheme } from '@mui/styles';
import { RICH_TEXT_FIELD_IDS } from 'features/srPanel/consts';
import BooleanField from 'features/srPanel/templateFieldsGrid/booleanField';
import { Timer } from 'common/components/timer';
import { useApplicationData } from 'remote-state/applicationHooks';
import { QUERIES_KEYS } from 'constant';
import * as PropTypes from 'prop-types';
import { SelectRender } from '../SelectRender';
import { AssigneeRender } from '../AssigneeRender';
import { DROP_DOWN_DELAY } from '../../constants';
import { CategoryRender } from '../CategoryRender/CategoryRender';
import { RichTextRender } from '../RichTextRender';
import OpenTicketCell from '../OpenTicket';
import IdFieldRender from '../IdFieldRender';
import useTexts from '../../useTexts';
import { TextAreaRender } from '../TextAreaRender';


TextAreaRender.propTypes = {
  dataTestId: PropTypes.string,
  className: PropTypes.string,
  fieldValue: PropTypes.string,
};
const CellComponent = memo((props) => {
  const {
    fieldTypeName,
    column,
    srId,
    activeField,
    hoverEffectText,
    handleFieldChange,
    handleInlineFieldChange,
    disableField,
    rowIndex,
    username,
    checkAvailableTicket,
    hoverEffectPointer,
    data,
    required,
    srStatusValue,
    gridApi,
  } = props;
  const theme = useTheme();
  const { data: isAiEnabled } = useApplicationData(QUERIES_KEYS.IS_AI_ENABLED);
  const { mandatoryFieldError } = useTexts();

  const selectRenderStyle = useMemo(
    () => ({
      fontSize: theme.fontSize.main,
      lineHeight: 1.429,
      color: theme.palette.text.txt_default,
    }),
    [theme],
  );

  const categoryRenderStyle = useMemo(
    () => ({
      fontSize: theme.fontSize.main,
      lineHeight: 1.429,
      color: theme.palette.text.txt_default,
    }),
    [theme],
  );

  const richTextFieldValue = useMemo(
    () => ({
      jsonContent: activeField?.jsonContent ? JSON.parse(activeField.jsonContent) : null,
      plainTextContent: activeField?.plainTextContent || null,
    }),
    [activeField],
  );

  const field = useMemo(
    () => ({
      fieldName: column?.colId,
      fieldId: column.colDef.fieldId,
      templateFieldAttributes: {
        ticketTemplateFieldType: {
          id: column.colDef.fieldTypeId,
        },
      },
    }),
    [column],
  );

  const getCellComponent = (fieldType) => {
    let multiline;

    switch (fieldType) {
      case 'BOOLEAN':
        return (
          <BooleanField
            field={field}
            required={required}
            fieldValue={activeField?.value}
            disabled={disableField}
            onChange={handleFieldChange}
            isTableView
          />
        );
      case 'TEXT':
        multiline = false;
        return (
          <InlineFieldContainer
            fullwidth
            limit={250}
            delayedHover={500}
            required={required}
            multiline={multiline}
            disabled={disableField}
            fieldName={column.colId}
            hoverEffect={hoverEffectText}
            tooltipClassName="inline-field-tooltip"
            handleSaveValue={handleInlineFieldChange}
            showSummaryTooltip={column.colId === 'title' && isAiEnabled}
            text={typeof activeField?.value === 'object' ? activeField?.value?.title : activeField?.value}
          />
        );
      case 'LONGTEXT':
        multiline = true;
        if (RICH_TEXT_FIELD_IDS.includes(column.colDef.fieldId)) {
          return (
            <RichTextRender
              srId={srId}
              required={required}
              className="isQueuePage"
              disabled={disableField}
              fieldName={column.colId}
              fieldId={column.colDef.fieldId}
              dataTestId="sr-description-editor"
              handleSaveValue={handleInlineFieldChange}
              fieldValue={richTextFieldValue}
            />
          );
        }
        return (
          <TextAreaRender
            fullwidth
            delayedHover={500}
            limit={5000}
            required={required}
            disabled={disableField}
            fieldName={column.colId}
            fieldId={column.colDef.fieldId}
            hoverEffect={hoverEffectText}
            multiline={multiline}
            handleSaveValue={handleInlineFieldChange}
            text={activeField?.value}
          />
        );
      case 'NUMBER':
        multiline = false;
        if (column.colDef.field === 'id') {
          return <IdFieldRender srId={srId} />;
        }
        return (
          <InlineFieldContainer
            fullwidth
            limit={250}
            type="number"
            delayedHover={500}
            required={required}
            multiline={multiline}
            disabled={disableField}
            fieldName={column.colId}
            text={activeField?.value}
            hoverEffect={hoverEffectText}
            handleSaveValue={handleInlineFieldChange}
          />
        );
      case 'DATE':
      case 'DATEANDTIME':
        return (
          <DatePicker
            fullwidth
            delayedHover={500}
            required={required}
            disablePortal={false}
            disabled={disableField}
            fieldType={column.colDef.fieldTypeId}
            fieldName={column.colId}
            date={activeField?.value}
            onChange={handleFieldChange}
            hoverEffect={hoverEffectPointer}
            emptyValueTooltip={mandatoryFieldError}
          />
        );
      case 'TIMER':
        return <Timer disabled={disableField} milliseconds={activeField?.value} />;
      case 'TIME_REMAINING': {
        const { endDate, startDate } = data.reduce(
          (result, fields) => {
            if (fields.field === 'dueDate') {
              result.endDate = fields;
            }
            if (fields.field === 'insertTime') {
              result.startDate = fields;
            }

            return result;
          },
          { endDate: undefined, startDate: undefined },
        );

        return (
          <DueDateField
            srId={srId}
            delayedHover={500}
            required={required}
            disabled={disableField}
            delay={DROP_DOWN_DELAY}
            endDate={endDate?.value}
            fieldName={column?.colId}
            startDate={startDate?.value}
            srStatusValue={srStatusValue}
            onSaveDate={handleFieldChange}
            hoverEffect={hoverEffectPointer}
            emptyValueTooltip={mandatoryFieldError}
          />
        );
      }
      case 'SELECT':
      case 'MULTISELECT': {
        const srType = data?.length > 0 ? data?.find((fields) => fields.field === 'srType')?.value : undefined;

        if (
          column.colDef.field === 'primaryCategory' ||
          column.colDef.field === 'secondaryCategory' ||
          column.colDef.field === 'thirdLevelCategory'
        ) {
          const category = data?.length > 0 ? data?.find((fields) => fields.field === props.column?.colId) : undefined;
          return (
            <CategoryRender
              srId={srId}
              srType={srType}
              delayedHover={500}
              rowData={category}
              required={required}
              disabled={disableField}
              delay={DROP_DOWN_DELAY}
              value={activeField?.value}
              fieldId={column.colDef.fieldId}
              hoverEffect={hoverEffectPointer}
              headerName={column.colDef.headerName}
              listKey={column.colDef.filterParams.listKey}
              style={categoryRenderStyle}
              rowIndex={rowIndex}
            />
          );
        }
        if (column.colDef.field !== 'assignee') {
          return (
            <SelectRender
              srId={srId}
              srType={srType}
              delayedHover={500}
              required={required}
              delay={DROP_DOWN_DELAY}
              disabled={disableField}
              hoverEffect={hoverEffectPointer}
              fieldId={column?.colDef?.fieldId}
              customColumn={column?.colDef?.customColumn}
              fieldName={column?.colDef?.field}
              headerName={column.colDef.headerName}
              keyString={column.colDef.keyString}
              captionString={column.colDef.captionString}
              isMultiple={fieldTypeName === 'MULTISELECT'}
              value={activeField?.value}
              style={selectRenderStyle}
              sortBy={column.colDef.sortBy}
              rowIndex={rowIndex}
            />
          );
        }
        return (
          <AssigneeRender
            srId={srId}
            delayedHover={500}
            required={required}
            disabled={disableField}
            hoverEffect={hoverEffectPointer}
            initialAssigned={activeField?.value}
            rowIndex={rowIndex}
          />
        );
      }
      case 'OPEN_LINK':
        return (
          <OpenTicketCell
            srId={srId}
            activeField={activeField}
            rowIndex={rowIndex}
            username={username}
            checkAvailableTicket={checkAvailableTicket}
            gridApi={gridApi}
          />
        );
      default:
        return (
          <Tooltip
            isTruncatedText
            placement="bottom"
            text={activeField?.value}
            style={{
              fontSize: theme.fontSize.main,
              lineHeight: 1.429,
              color: theme.palette.text.txt_default,
            }}
          />
        );
    }
  };
  return getCellComponent(fieldTypeName);
});

export default CellComponent;
